import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LANG_OPTIONS } from "../i18n";

export const Navigation = ({ onWrite }) => {
  const { t, i18n } = useTranslation();
  const current = i18n.language;
  const [open, setOpen] = useState(false);

  const setLang = (code) => {
    i18n.changeLanguage(code);
    document.documentElement.setAttribute("lang", code);
  };

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const closeAnd = (fn) => () => {
    setOpen(false);
    if (typeof fn === "function") {
      // small delay so drawer can begin to close before scrolling/jumping
      setTimeout(fn, 60);
    }
  };

  return (
    <header
      className="w-full border-b border-[var(--rule)] bg-[var(--bg-paper)]/85 backdrop-blur sticky top-0 z-30"
      data-testid="site-header"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 md:py-5 flex items-center justify-between gap-3 md:gap-0">
        {/* Brand — always visible. On desktop it takes the left third. */}
        <a href="#top" className="flex items-center gap-3 min-w-0 md:flex-1 md:justify-start" data-testid="brand-link">
          <img
            src="/press-kit/logo-192.png"
            alt="MiProse wax seal"
            width="42"
            height="42"
            className="shrink-0 object-contain"
            style={{
              width: 42,
              height: 42,
              padding: 0,
              imageRendering: "-webkit-optimize-contrast",
            }}
          />
          <span className="font-display text-lg sm:text-xl tracking-tight truncate">
            {t("nav.brand")}
          </span>
        </a>

        {/* ───── Desktop (md+) center column ───── */}
        <nav
          className="hidden md:flex md:flex-1 md:justify-center items-center gap-8 text-[15px] text-[var(--ink-soft)]"
          aria-label="primary"
        >
          <a
            href="#story"
            className="hover:text-[var(--wax)] transition-colors"
            data-testid="nav-story"
          >
            {t("nav.story")}
          </a>
          <a
            href="#faq"
            className="hover:text-[var(--wax)] transition-colors"
            data-testid="nav-faq"
          >
            {t("nav.faq")}
          </a>
        </nav>

        {/* ───── Desktop right column (lang + write) ───── */}
        <div className="hidden md:flex md:flex-1 md:justify-end items-center gap-2" role="group" aria-label="language">
          {LANG_OPTIONS.map((opt) => (
            <button
              key={opt.code}
              data-testid={`lang-${opt.code}`}
              onClick={() => setLang(opt.code)}
              className={`whitespace-nowrap px-2.5 py-1 text-[13px] tracking-widest transition-colors ${
                current === opt.code
                  ? "text-[var(--wax)] border-b border-[var(--wax)]"
                  : "text-[var(--ink-soft)] hover:text-[var(--ink)]"
              }`}
            >
              {opt.label}
            </button>
          ))}
          <button
            onClick={onWrite}
            data-testid="nav-write-btn"
            className="ml-4 btn-wax px-5 py-2.5 text-[13px] uppercase tracking-[0.18em] whitespace-nowrap"
          >
            {t("nav.write")}
          </button>
        </div>

        {/* ───── Mobile hamburger trigger (below md) ───── */}
        <button
          type="button"
          aria-label="Open menu"
          aria-expanded={open}
          aria-controls="mobile-drawer"
          onClick={() => setOpen(true)}
          data-testid="nav-mobile-toggle"
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-[var(--rule)] text-[var(--ink)] hover:text-[var(--wax)] hover:border-[var(--wax)] transition-colors shrink-0"
        >
          {/* hand-drawn 3-line icon */}
          <svg width="20" height="14" viewBox="0 0 20 14" fill="none" aria-hidden="true">
            <path d="M1 1.5 H19" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            <path d="M1 7   H15" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            <path d="M1 12.5 H19" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* ───── Mobile drawer overlay ───── */}
      <div
        className={`md:hidden fixed inset-0 z-40 transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!open}
      >
        {/* backdrop */}
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setOpen(false)}
          data-testid="nav-mobile-backdrop"
          className="absolute inset-0 bg-[var(--ink)]/45 backdrop-blur-sm"
        />

        {/* drawer panel — anchored to top, slides down */}
        <div
          id="mobile-drawer"
          role="dialog"
          aria-modal="true"
          data-testid="nav-mobile-drawer"
          className={`absolute top-0 left-0 right-0 bg-[var(--bg-paper)] border-b border-[var(--rule)] shadow-[0_24px_48px_-24px_rgba(28,26,23,0.35)] transition-transform duration-300 ease-out ${
            open ? "translate-y-0" : "-translate-y-full"
          }`}
        >
          {/* drawer header (brand row + close) */}
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <img
                src="/press-kit/logo-192.png"
                alt="MiProse wax seal"
                width="38"
                height="38"
                className="shrink-0 object-contain"
                style={{
                  width: 38,
                  height: 38,
                  imageRendering: "-webkit-optimize-contrast",
                }}
              />
              <span className="font-display text-lg tracking-tight truncate">{t("nav.brand")}</span>
            </div>
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-close"
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[var(--rule)] text-[var(--ink)] hover:text-[var(--wax)] hover:border-[var(--wax)] transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M1 1 L13 13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                <path d="M13 1 L1 13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className="max-w-6xl mx-auto px-6 pb-8 pt-2 flex flex-col gap-7">
            {/* Primary CTA */}
            <button
              onClick={closeAnd(onWrite)}
              data-testid="nav-mobile-write-btn"
              className="btn-wax w-full py-4 text-[13px] uppercase tracking-[0.22em] min-h-[52px]"
            >
              {t("nav.write")}
            </button>

            {/* Page links */}
            <nav className="flex flex-col" aria-label="mobile-primary">
              <a
                href="#story"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-story"
                className="block py-3.5 text-[17px] text-[var(--ink)] border-b border-[var(--rule)] hover:text-[var(--wax)] transition-colors"
              >
                {t("nav.story")}
              </a>
              <a
                href="#faq"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-faq"
                className="block py-3.5 text-[17px] text-[var(--ink)] border-b border-[var(--rule)] hover:text-[var(--wax)] transition-colors"
              >
                {t("nav.faq")}
              </a>
            </nav>

            {/* Language pills */}
            <div>
              <p className="text-[10px] uppercase tracking-[0.4em] text-[var(--ink-mute)] mb-3">
                Language · 語言
              </p>
              <div className="flex flex-wrap gap-2" role="group" aria-label="language-mobile">
                {LANG_OPTIONS.map((opt) => (
                  <button
                    key={opt.code}
                    data-testid={`nav-mobile-lang-${opt.code}`}
                    onClick={() => {
                      setLang(opt.code);
                    }}
                    className={`px-4 py-2 text-[13px] tracking-[0.18em] rounded-full border transition-colors min-h-[40px] ${
                      current === opt.code
                        ? "bg-[var(--wax)] text-[var(--bg-paper)] border-[var(--wax)]"
                        : "text-[var(--ink-soft)] border-[var(--rule)] hover:border-[var(--wax)] hover:text-[var(--wax)]"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
